/**
 * compressImage (field media Phase 2) — capture-time downscale + JPEG re-encode
 * for the field zone. Field-local by design: imports nothing outside
 * src/features/field/**, and deliberately does NOT reuse offlineEngine's
 * module-private ensureJpeg (that helper is unexported, does not resize, and
 * serves the prod disposal path — sharing it would couple two zones for no gain).
 *
 * Contract:
 *   compressImage(file) -> Promise<{ blob, wasCompressed, reason }>
 *     blob          compressed JPEG Blob, or the ORIGINAL input unchanged on any failure
 *     wasCompressed boolean
 *     reason        null on success; short slug on fail-open
 *
 * FAILS OPEN, ALWAYS. Every path is wrapped: any throw, any unsupported input,
 * any zero-dimension decode, any null encode returns the original bytes with a
 * reason. It never throws, never returns null, and never returns a partial
 * result. A photo is worth more than the bytes an optimization would save.
 *
 * ORIENTATION — the highest-risk part of this module. A canvas re-encode strips
 * EXIF, so the pixels must already be rotated before they reach the canvas or
 * the output is silently sideways. Two decode paths, in this order:
 *
 *   1. createImageBitmap(file, { imageOrientation: 'from-image' }) — rotates
 *      during decode.
 *   2. <img> + drawImage — NOT a safe path, only a less-bad one. drawImage
 *      began honoring EXIF orientation in Chrome/Safari only around 2020; on
 *      an older WebView it samples the raw, un-oriented pixels and the output
 *      is sideways with no error anywhere.
 *
 * So BOTH decode paths carry orientation risk, and the fallback carries more.
 * Phase 3's real-device cell exercises Path 1 only — Path 2 will remain
 * UNPROVEN after Phase 3 signs off.
 *
 * We still NEVER fall back to a bare createImageBitmap(file) with no options
 * bag: that path is explicitly un-oriented on every engine, so it would trade a
 * thrown error for a certainly rotated photo. The <img> path is the only
 * fallback.
 *
 * ⚠️ LIMIT OF THE FEATURE TEST: probing by try/catch catches engines that THROW
 * on the options bag. It cannot catch an engine that ACCEPTS the bag and
 * silently ignores it — that returns a valid, un-rotated bitmap and looks like
 * success from here. Detecting that needs a known EXIF-rotated fixture image
 * and a dimension-swap check, which is not worth embedding. The residual risk
 * is real and is exactly what the Phase 3 real-device portrait cell exists to
 * catch. Nothing in this file proves orientation is correct.
 */

/** Longest edge, in px, of the compressed output. Scale DOWN only. */
const MAX_EDGE = 1600;

/** JPEG encode quality for the compressed output. */
const JPEG_QUALITY = 0.7;

/** Uniform fail-open shape — the original bytes, untouched, plus a reason. */
function failOpen(file, reason) {
  return { blob: file, wasCompressed: false, reason };
}

/**
 * Decode to something drawImage accepts, with EXIF orientation already applied.
 * Returns { source, bitmap } where bitmap is non-null only when the
 * createImageBitmap path won (the caller must close it). Throws on total
 * failure; the caller owns the fail-open.
 */
async function decodeOriented(file) {
  // Path 1 — createImageBitmap with the orientation option. Probed by USE, not
  // by `typeof`: an engine can expose the function and still reject the bag.
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, bitmap };
    } catch (e) {
      // Threw on the options bag, or could not decode this type. Fall through
      // to the <img> path rather than retrying without the bag.
    }
  }

  // Path 2 — <img> + object URL. Revoked in the caller's finally.
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('img decode failed'));
      el.src = url;
    });
    return { source: img, bitmap: null, url };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/** canvas.toBlob promisified. Resolves null when the engine declines to encode. */
function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
  });
}

/**
 * @param {File|Blob} file
 * @returns {Promise<{blob: Blob, wasCompressed: boolean, reason: string|null}>}
 */
export async function compressImage(file) {
  let decoded = null;
  let canvas = null;
  try {
    if (!file || typeof file !== 'object' || typeof file.type !== 'string') {
      return failOpen(file, 'not_an_image');
    }
    if (!file.type.toLowerCase().startsWith('image/')) {
      // iOS Safari sometimes hands over a File with an EMPTY .type. The caller's
      // accept gate already vouched for those, so an empty type is allowed
      // through to the decoder — only a non-empty NON-image type is refused.
      if (file.type !== '') return failOpen(file, 'not_an_image');
    }

    decoded = await decodeOriented(file);
    const { source } = decoded;

    const srcW = source.width || source.naturalWidth || 0;
    const srcH = source.height || source.naturalHeight || 0;
    if (!srcW || !srcH) return failOpen(file, 'decode_failed');

    // Scale DOWN only — a smaller image keeps its dimensions and is re-encoded
    // for bytes alone.
    const scale = Math.min(1, MAX_EDGE / Math.max(srcW, srcH));
    const outW = Math.max(1, Math.round(srcW * scale));
    const outH = Math.max(1, Math.round(srcH * scale));

    canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return failOpen(file, 'no_canvas_context');
    ctx.drawImage(source, 0, 0, outW, outH);

    const blob = await canvasToBlob(canvas);
    if (!blob || !blob.size) return failOpen(file, 'encode_failed');

    // Re-encoding can GROW the input — a small PNG screenshot, a flat-colour
    // diagram, artwork that JPEG handles badly. Only a downscale guarantees a
    // win, and an image already under MAX_EDGE gets no downscale. Ship whichever
    // is smaller: a larger "compressed" result is a regression wearing the
    // wrong label.
    if (typeof file.size === 'number' && blob.size >= file.size) {
      return failOpen(file, 'no_gain');
    }

    return { blob, wasCompressed: true, reason: null };
  } catch (e) {
    // Blanket fail-open. Nothing above is allowed to reach the caller as a throw.
    return failOpen(file, 'compress_threw');
  } finally {
    // Release the decode source. ImageBitmap holds real memory until closed,
    // and an un-revoked object URL leaks for the life of the document.
    try {
      if (decoded?.bitmap) decoded.bitmap.close();
      if (decoded?.url) URL.revokeObjectURL(decoded.url);
    } catch (e) {
      /* cleanup must never mask the result */
    }
    // Zeroing the canvas releases its backing store promptly — iOS Safari is
    // slow to reclaim large canvases and a capture burst can exhaust memory.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
      canvas = null;
    }
  }
}

export default compressImage;
